/**
 * 4-player Room schema —— SocialSlots 的核心差异化
 *
 * 一个 Room：
 *  - 至多 4 个 member（玩家或 NPC bot）
 *  - 锁定一个 game_id（candy / sakura）
 *  - 共享 Heat Bar（玩家中奖共同推进）
 *  - 满 100 → Lucky Fever 共同事件（10 spin 全房 ×2 multiplier）
 */
import { z } from 'zod';
import { GameId } from './games.js';
export const ROOM_MAX_MEMBERS = 4;
export const HEAT_BAR_MAX = 100;
/** Lucky Fever 持续时间（per math/05 spec：120 秒，4 人同步参与） */
export const LUCKY_FEVER_DURATION_MS = 120 * 1000;
/** 连中 3 次（per spec §5.2）→ heat +5 */
export const CONSECUTIVE_WINS_HEAT_BONUS = 5;
export const CONSECUTIVE_WINS_THRESHOLD = 3;
/** Member 实时状态（由后端基于 last_spin_at / FS session 推导，前端用于 status ring） */
export const MemberStatus = z.enum(['spinning', 'idle', 'bonus']);
export const RoomMember = z.object({
    user_id: z.string(),
    display_name: z.string(),
    avatar_url: z.string().nullable(),
    is_synthetic: z.boolean(), // NPC bot 标记（per ADR-006）
    joined_at: z.string(), // ISO timestamp
    last_spin_at: z.string().nullable(),
    total_room_win: z.number().int().nonnegative(), // 玩家在本 room 内累计赢额
    total_room_spin_count: z.number().int().nonnegative(),
    status: MemberStatus, // 由后端推导：spinning（刚 spin 内 2.5s）/ bonus（FS 中）/ idle
    /** Lucky Fever 期间累计 bet 量（贡献度）—— 用于 fever 结束时分配 pool */
    contribution_in_current_fever: z.number().int().nonnegative(),
    /** 最近一次 spin 的 win 数额（>=0），UI 用作 mini pill 显示 */
    last_win_amount: z.number().int().nonnegative(),
    /** 最近一次有效 spin 的 bet 数额（FS 0 spin 不更新）；UI 在 idle 状态显示「$bet」让玩家看到每个座位下注不同 */
    last_bet_amount: z.number().int().nonnegative().default(0),
    /** 当前虚拟余额（decorate 时从 wallet 注入）；UI 显示让玩家看到每个 seat 资金量级不同 */
    balance: z.number().int().nonnegative().default(0),
});
/** 快捷互动（Fire / Nice / Lucky）—— P0 三按钮 */
export const ReactionEmoji = z.enum(['fire', 'nice', 'lucky']);
export const RoomReaction = z.object({
    ts: z.string(),
    user_id: z.string(),
    display_name: z.string(),
    emoji: ReactionEmoji,
    /** 可选：定向 cheer 给某座位（玩家点击对方头像时填）；空 = 全房广播 */
    target_user_id: z.string().nullable(),
});
export const RoomEvent = z.object({
    ts: z.string(), // ISO timestamp
    user_id: z.string(),
    display_name: z.string(),
    type: z.enum(['win', 'big_win', 'mega_win', 'super_win', 'jackpot', 'free_spins_trigger', 'fever_start', 'fever_end', 'pool_reward', 'join', 'leave']),
    amount: z.number().int().nonnegative().optional(), // 中奖额 / pool 分账额
    heat_delta: z.number().nonnegative().optional(), // 此事件推 heat bar 多少
});
/** Lucky Fever 结束后的奖池分配快照（per HANDOFF 01_VISION "Lucky Fever Ended — Total Pool: <N>"） */
export const FeverSummary = z.object({
    ended_at: z.string(),
    total_pool: z.number().int().nonnegative(),
    distribution: z.array(z.object({
        user_id: z.string(),
        display_name: z.string(),
        contribution: z.number().int().nonnegative(),
        award: z.number().int().nonnegative(),
    })),
});
export const RoomState = z.object({
    id: z.string(),
    game_id: GameId,
    members: z.array(RoomMember),
    heat: z.number().nonnegative(), // 0-100
    heat_max: z.number().int().positive(),
    /** FTUE / onboarding room marker. This controls room-event pacing only, never RNG/RTP. */
    is_onboarding: z.boolean().default(false),
    heat_rigging_coefficient: z.number().min(0).max(1).default(0),
    onboarding_stage: z.enum(['first_session', 'sessions_2_3', 'd2_returner', 'steady']).default('steady'),
    /** Set of real-member user_ids that triggered FTUE state on this room. When all
     *  of them leave, the room reverts to steady (rigging=0). Bots are never in
     *  this set — they don't carry FTUE entitlement. Empty array when steady. */
    onboarding_member_ids: z.array(z.string()).default([]),
    fever_active: z.boolean(),
    /** Fever 结束时间（ISO ts，120s 倒计时用）；fever_active=false 时为 null */
    fever_ends_at: z.string().nullable(),
    /** 客户端便利字段：剩余秒数（后端每次响应时计算），fever_active=false 时为 0 */
    fever_seconds_remaining: z.number().nonnegative(),
    recent_events: z.array(RoomEvent), // 最近 20 条
    reactions: z.array(RoomReaction), // 最近 10 条快捷互动
    /** 上一次 Lucky Fever 结束的分账快照；客户端用于弹「Total Pool / Split」结算卡 */
    last_fever_summary: FeverSummary.nullable(),
    created_at: z.string(),
    last_activity_at: z.string(),
});
// ─── API requests ──────────────────────────────────────────
export const CreateRoomRequest = z.object({
    game_id: GameId,
});
export const JoinRoomRequest = z.object({
    room_id: z.string(),
});
export const FindRoomRequest = z.object({
    game_id: GameId,
    /** 找不到 game_id 的活跃房间时是否自动创建 */
    auto_create: z.boolean().default(true),
});
export const ReactRequest = z.object({
    emoji: ReactionEmoji,
    target_user_id: z.string().nullable().optional(),
});
// ─── Heat Bar 数值表 ───────────────────────────────────────
//
// 不同等级中奖推 heat bar 多少。设计思路：
//  - 普通小奖：heat += 1（很多次才能填满）
//  - big：+ 4
//  - mega：+ 12
//  - super：+ 30
//  - jackpot：+ 60
//  - free spins trigger：+ 8（即使没中奖）
// 4 人房平均节奏：每分钟 4 人共 spin ~80 次 → 平均 ~24 个小奖（每分钟 +24 heat）
// → 约每 4-5 分钟触发一次 Lucky Fever
//
// Heat 数值对齐 math/05 spec §5.2，tier 之间梯度合理
export const HEAT_PER_WIN_TIER = {
    small: 1,
    big: 8,
    mega: 20,
    super: 35,
    jackpot: 70,
    free_spins_trigger: 15,
};
export const LUCKY_FEVER_BUFF = {
    win_multiplier: 1.5, // per HANDOFF/01_VISION Day-1 walkthrough §03:35
    multiplier_symbol_chance_boost: 1.5,
    scatter_chance_boost: 1.2,
};
/** Lucky Fever 结束 pool 计算：fever 期间所有 member 累计 contribution 的 50%。
 *  设计意图：fever 是「集体奖励池」，由 fever 期间下注量驱动，结束时按贡献比例返还 50% 作为 tip pool。 */
export const FEVER_POOL_RATE = 0.5;
//# sourceMappingURL=room.js.map